import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';

export type FooterConfig = {
  onBack?: () => void;
  onNext?: () => void;
  backText?: string;
  nextText?: string;
  nextDisabled?: boolean;
  nextDisabledReason?: string;
  nextVariant?: 'blue' | 'green';
  showBackArrow?: boolean;
  // Passive inline decoration rendered after the next button's text
  // (AHA-6681 sub-build 5: the pre-warm readiness spinner/check). Purely
  // informational — it never affects the button's enabled state.
  nextAdornment?: ReactNode;
  alternateNextButton?: {
    text: string;
    onClick: () => void;
    disabled?: boolean;
    disabledReason?: string;
  };
  // Secondary blue button left of the next button (AHA-6681 tonnage §F:
  // "Get a Detailed Sizing Estimate" while the skip path owns the primary).
  secondaryButton?: {
    text: string;
    onClick: () => void;
    tooltip?: string;
  };
};

type FooterContextType = {
  config: FooterConfig;
  setConfig: (config: FooterConfig) => void;
};

const FooterContext = createContext<FooterContextType>({
  config: {},
  setConfig: () => {}
});

export function FooterProvider({ children }: { readonly children: ReactNode }) {
  const [config, setConfig] = useState<FooterConfig>({});

  const value = useMemo(() => ({ config, setConfig }), [config]);

  return (
    <FooterContext.Provider value={value}>{children}</FooterContext.Provider>
  );
}

export function useFooterConfig(config: FooterConfig) {
  const { setConfig } = useContext(FooterContext);
  const configRef = useRef(config);
  configRef.current = config;

  const stableSetConfig = useCallback(setConfig, [setConfig]);

  useEffect(() => {
    stableSetConfig(configRef.current);
  });

  useEffect(() => {
    return () => stableSetConfig({});
  }, [stableSetConfig]);
}

export function useFooterContext() {
  return useContext(FooterContext).config;
}
